import { Text } from '@/components/ui/text'
import { MicaIndicator, MicaIndicatorObject } from './mica'
import { CloudUploadIcon, EarthIcon, InfoIcon, TrashIcon, BatteryChargingIcon } from 'lucide-react'
import { cx } from 'class-variance-authority'
import { cn } from '@/utilities/ui'
import { Tooltip } from '@/components/ui/tooltip'

type MicaComplianceTableCellGroupLayoutProps = {
  indicatorData: MicaIndicator
  displayValue: string
  customTitle?: string
  last?: boolean
}

function MicaComplianceTableCellGroupLayout({
  indicatorData,
  displayValue,
  customTitle,
  last,
}: MicaComplianceTableCellGroupLayoutProps) {
  const { title, description } = indicatorData
  return (
    <>
      <td className={cx('text-left py-4', !last && 'border-b-2 border-olive-1/10')}>
        <Text size="sm" className="pl-2 md:pl-0">
          {customTitle || title}
        </Text>
      </td>
      <td className={cx('text-left py-4', !last && 'border-b-2 border-olive-1/10')}>
        <div className="flex justify-end items-center gap-2 pr-2">
          <Text size="sm" className="truncate text-nowrap">
            {displayValue}
          </Text>
          <Tooltip tip={description}>
            <InfoIcon className="hover:cursor-pointer text-olive-2" />
          </Tooltip>
        </div>
      </td>
    </>
  )
}

type MicaComplianceTableProps = {
  className?: string
  micaIndicators: MicaIndicatorObject
}

export function MicaComplianceTable({ micaIndicators, className }: MicaComplianceTableProps) {
  return (
    <div className={cn('border border-olive-1/10 p-4', className)}>
      <table className="table-auto border-collapse w-full">
        <thead className="bg-paper-2">
          <tr className="border-b-2 border-olive-1/10">
            <th className="py-6 hidden md:table-cell">
              <Text size="lg" className="font-medium">
                Domain
              </Text>
            </th>
            <th className="text-left py-6">
              <Text size="lg" className="pl-2 md:pl-0 font-medium">
                Sustainability Indicator
              </Text>
            </th>
            <th className="py-6"></th>
          </tr>
        </thead>
        <tbody className="text-center">
          <tr>
            <td className="py-4 border-b-2 border-olive-1/10 hidden md:table-cell " rowSpan={3}>
              <div className="flex flex-col justify-center items-center gap-4 py-[20px]">
                <BatteryChargingIcon className="size-[30px] text-olive-2" />
                <Text size="lg" className="font-medium">
                  Energy
                </Text>
              </div>
            </td>
            <MicaComplianceTableCellGroupLayout
              indicatorData={micaIndicators['indicator_1']}
              displayValue={`${Math.round(
                micaIndicators['indicator_1'].result.value,
              ).toLocaleString()}${' '}${micaIndicators['indicator_1'].unit.replace('kwh', 'kWh')}`}
            />
          </tr>
          <tr>
            <MicaComplianceTableCellGroupLayout
              indicatorData={micaIndicators['indicator_2']}
              displayValue={`${micaIndicators['indicator_2'].result.value.toFixed(2)}%`}
            />
          </tr>
          <tr>
            <MicaComplianceTableCellGroupLayout
              indicatorData={micaIndicators['indicator_3']}
              displayValue={`${micaIndicators['indicator_3'].result.value.toFixed(6)}${' '}
                  ${micaIndicators['indicator_3'].unit.replace('kwh', 'kWh')}`}
            />
          </tr>
          <tr>
            <td className="py-4 border-b-2 border-olive-1/10 hidden md:table-cell" rowSpan={3}>
              <div className="flex flex-col justify-center items-center gap-4 py-[20px]">
                <CloudUploadIcon className="size-[30px] text-olive-2" />
                <Text size="lg" className="font-medium">
                  GHG Emissions
                </Text>
              </div>
            </td>
            <MicaComplianceTableCellGroupLayout
              indicatorData={micaIndicators['indicator_4']}
              customTitle="Scope 1 - Controlled"
              displayValue={`${micaIndicators['indicator_4'].result.value}${' '}
                  ${micaIndicators['indicator_4'].unit}`}
            />
          </tr>
          <tr>
            <MicaComplianceTableCellGroupLayout
              indicatorData={micaIndicators['indicator_5']}
              customTitle="Scope 2 - Purchased"
              displayValue={`${micaIndicators['indicator_5'].result.value.toLocaleString()}${' '}
                  ${micaIndicators['indicator_5'].unit}`}
            />
          </tr>
          <tr>
            <MicaComplianceTableCellGroupLayout
              indicatorData={micaIndicators['indicator_6']}
              displayValue={`${micaIndicators['indicator_6'].result.value.toFixed(6)}${' '}
                  ${micaIndicators['indicator_6'].unit}`}
            />
          </tr>
          <tr>
            <td className="py-4 border-b-2 border-olive-1/10 hidden md:table-cell" rowSpan={3}>
              <div className="flex flex-col justify-center items-center gap-4 py-[20px]">
                <TrashIcon className="size-[30px] text-olive-2" />
                <Text size="lg" className="font-medium">
                  Waste Production
                </Text>
              </div>
            </td>
            <MicaComplianceTableCellGroupLayout
              indicatorData={micaIndicators['indicator_7']}
              displayValue={`${micaIndicators['indicator_7'].result.value.toFixed(2)}${' '}
                  ${micaIndicators['indicator_7'].unit}`}
            />
          </tr>
          <tr>
            <MicaComplianceTableCellGroupLayout
              indicatorData={micaIndicators['indicator_8']}
              displayValue={`${micaIndicators['indicator_8'].result.value.toFixed(2)}%`}
            />
          </tr>
          <tr>
            <MicaComplianceTableCellGroupLayout
              indicatorData={micaIndicators['indicator_9']}
              displayValue={`${micaIndicators['indicator_9'].result.value.toFixed(6)}${' '}
                  ${micaIndicators['indicator_9'].unit}`}
            />
          </tr>
          <tr>
            <td className="py-4 hidden md:table-cell">
              <div className="flex flex-col justify-center items-center gap-4 py-[20px]">
                <EarthIcon className="size-[30px] text-olive-2" />
                <Text size="lg" className="font-medium">
                  Natural resources
                </Text>
              </div>
            </td>
            <MicaComplianceTableCellGroupLayout
              indicatorData={micaIndicators['indicator_10']}
              displayValue={`${Math.round(
                micaIndicators['indicator_10'].result.value,
              ).toLocaleString()}${' '}
                  ${micaIndicators['indicator_10'].unit}`}
              last
            />
          </tr>
        </tbody>
      </table>
    </div>
  )
}
